import {
  Adjust as AdjustCordova,
  AdjustEvent as AdjustEventCordova,
} from '@awesome-cordova-plugins/adjust'
import AdjustWeb from '@adjustcom/adjust-web-sdk'

import { waitWithTimeout } from '@/helpers/timer'
import { Sentry } from '@/services/sentry'
import { platform } from '@/services/platform'

const SIGNUP_EVENT_TOKENS = {
  ios: '8l934d',
  android: '9a66cm',
  web: 'dxlnaq',
}

const TRIAL_START_EVENT_TOKENS = {
  ios: 'a2db57',
  android: 'gi9lxv',
  web: 'yunkhm',
}

const PAGE_VIEW_HOMEPAGE_EVENT_TOKENS = {
  web: 'kh0xz4',
}

const PAGE_VIEW_SIGNUP_EVENT_TOKENS = {
  web: 'mt06ut',
}

/**
 * Track the Page View (Homepage) event.
 */
export async function trackPageViewHomepage(): Promise<void> {
  const info = await platform.deviceInfo()
  if (info.platform === 'web') {
    try {
      AdjustWeb.trackEvent({ eventToken: PAGE_VIEW_HOMEPAGE_EVENT_TOKENS.web })
    } catch (error) {
      Sentry.captureException(error)
    }
  }
}

/**
 * Track the Page View (Signup) event.
 */
export async function trackPageViewSignup(): Promise<void> {
  const info = await platform.deviceInfo()
  if (info.platform === 'web') {
    try {
      AdjustWeb.trackEvent({ eventToken: PAGE_VIEW_SIGNUP_EVENT_TOKENS.web })
    } catch (error) {
      Sentry.captureException(error)
    }
  }
}

/**
 * Track the Signup event.
 */
export async function register(): Promise<void> {
  const info = await platform.deviceInfo()
  if (info.platform === 'ios' || info.platform === 'android') {
    const eventToken = SIGNUP_EVENT_TOKENS[info.platform]
    try {
      const event = new AdjustEventCordova(eventToken)
      AdjustCordova.trackEvent(event)
    } catch (error) {
      Sentry.captureException(error)
    }
  } else {
    try {
      AdjustWeb.trackEvent({ eventToken: SIGNUP_EVENT_TOKENS.web })
    } catch (error) {
      Sentry.captureException(error)
    }
  }
}

/**
 * Track the Trial Start event. This is equivalent to Card Save in Mixpanel.
 */
export async function trackCardSave(): Promise<void> {
  const info = await platform.deviceInfo()
  if (info.platform === 'ios' || info.platform === 'android') {
    const eventToken = TRIAL_START_EVENT_TOKENS[info.platform]
    try {
      const event = new AdjustEventCordova(eventToken)
      AdjustCordova.trackEvent(event)
    } catch (error) {
      Sentry.captureException(error)
    }
  } else {
    try {
      AdjustWeb.trackEvent({ eventToken: TRIAL_START_EVENT_TOKENS.web })
    } catch (error) {
      Sentry.captureException(error)
    }
  }
}

/**
 * Get the Adjust identifier. This is an identifier generated by Adjust that is
 * unique to each device.
 */
export async function getAdid(): Promise<string> {
  const info = await platform.deviceInfo()
  if (info.platform === 'ios' || info.platform === 'android') {
    return await AdjustCordova.getAdid()
  }
  let adidWeb = ''
  // If there is no attribution info, then waitForAttribution never resolves.
  // To avoid waiting infinitely in that case, we use a timeout.
  await waitWithTimeout(
    (done) => {
      AdjustWeb.waitForAttribution().then((attributionInfo) => {
        adidWeb = attributionInfo.adid
        done()
      })
    },
    1,
    1000,
  )
  return adidWeb
}

/**
 * Get the IDFA (iOS advertising identifier) if we are on iOS.
 */
export async function maybeGetIdfa(): Promise<string> {
  const info = await platform.deviceInfo()
  if (info.platform === 'ios') {
    return await AdjustCordova.getIdfa()
  }
  return ''
}

/**
 * Get the GPS ADID (Google Play advertising identifier) if we are on Android.
 */
export async function maybeGetGpsAdid(): Promise<string> {
  const info = await platform.deviceInfo()
  if (info.platform === 'android') {
    return await AdjustCordova.getGoogleAdId()
  }
  return ''
}

/*
 * Adjust helps us track mobile app events so that we can use Apple Search Ads.
 * We name this "AdjustWrapper" to avoid confusion with the actual Adjust SDK.
 *
 * Cordova plugin docs:
 * https://github.com/adjust/cordova_sdk/blob/master/README.md
 * https://github.com/danielsogl/awesome-cordova-plugins/blob/master/src/%40awesome-cordova-plugins/plugins/adjust/index.ts
 */
export * as AdjustWrapper from './adjust'
